import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt,
  FaOm, FaPray, FaAnchor
} from 'react-icons/fa';
import './KashiAyodhya.css';

const KashiAyodhya = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  // KASHI AYODHYA ITINERARY DATA
  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Varanasi & Ganga Aarti",
      points: [
        "Arrival in Varanasi, transfer to the hotel and check-in.",
        "Evening: Proceed to Dashashwamedh Ghat to witness the world-famous Ganga Aarti.",
        "Experience one of the most mesmerizing spiritual ceremonies in India.",
        "Enjoy a walk along the ghats and explore nearby markets and temple lanes.",
        "Dinner and overnight stay in Varanasi."
      ],
      icon: <FaPray />
    },
    {
      day: 2,
      title: "Sunrise Boat Ride & Kashi Vishwanath Darshan",
      points: [
        "Early morning sunrise boat ride on the holy Ganga River to witness morning rituals.",
        "Return to hotel for breakfast.",
        "Temple Darshan: Visit Kashi Vishwanath Temple, Annapurna Temple, and Vishalakshi Temple.",
        "Explore the Kashi Vishwanath Corridor area comfortably.",
        "Evening free for leisure or exploring nearby ghats.",
        "Dinner and overnight stay in Varanasi."
      ],
      icon: <FaAnchor />
    },
    {
      day: 3,
      title: "Varanasi Cultural Exploration",
      points: [
        "Visit Assi Ghat, Banaras Hindu University (BHU), and the New Vishwanath Temple.",
        "Explore Banarasi silk saree markets and traditional handicraft lanes.",
        "Optional: Attend the rare midnight Aarti at Kal Bhairav Temple (12:30 AM – 1:00 AM).",
        "Experience the powerful spiritual energy of the guardian deity of Kashi.",
        "Dinner and overnight stay in Varanasi."
      ],
      icon: <FaOm />
    },
    {
      day: 4,
      title: "Varanasi to Ayodhya (Divine Journey)",
      points: [
        "Drive to Ayodhya (Approx. 220 km | 4–5 hours).",
        "Check in to the hotel upon arrival.",
        "Darshan at Ram Janmabhoomi Temple, Hanuman Garhi, and Kanak Bhawan.",
        "Visit the sacred Saryu River ghats in the evening.",
        "Dinner and overnight stay in Ayodhya."
      ],
      icon: <FaBus />
    },
    {
      day: 5,
      title: "Ayodhya to Varanasi Return",
      points: [
        "Visit additional sacred places: Ram Ki Paidi and Nageshwarnath Temple.",
        "Drive back to Varanasi after the morning visits.",
        "Evening free for leisure or last-minute local market exploration.",
        "Dinner and overnight stay in Varanasi."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 6,
      title: "Departure",
      points: [
        "Breakfast and hotel check-out.",
        "Transfer to Varanasi Airport / Railway Station for your onward journey.",
        "Tour concludes with beautiful spiritual memories of Kashi and Ayodhya."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Kashi Ayodhya Spiritual Journey – <br /> Varanasi & Ayodhya</h1>
          <p className="trip-duration">Duration: 5 nights & 6 days of Divine Exploration</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Varanasi, Ayodhya</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Spiritual & Cultural Tour</p></div></div>
          <div className="info-row type-tag"><FaOm className="header-icon" /><p>Sacred Pilgrimage</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://images.unsplash.com/photo-1706186839147-0d708602587b?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Varanasi Ghats" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://images.unsplash.com/photo-1706169599121-4182eb12fbef?q=80&w=261&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Ayodhya Ram Mandir" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1716573260891-23ad993e8833?q=80&w=1031&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Ganga Aarti" />
          </div>
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1549225480-ce72840aa6c8?q=80&w=854&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Saryu River" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>Overview</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Day Wise Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Sunrise boat ride on the holy Ganges River</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Darshan at Kashi Vishwanath, Annapurna & Vishalakshi Temples</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Witness the magnificent Ganga Aarti at Dashashwamedh Ghat</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Darshan at the sacred Ram Janmabhoomi Temple in Ayodhya</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit Hanuman Garhi and the sacred Saryu River ghats</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Optional midnight Aarti experience at Kal Bhairav Temple</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore the traditional lanes and silk markets of Banaras</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> Accommodation in hotels as per itinerary</li>
              <li><FaUtensils /> Meal Plan: Daily Breakfast and Dinner at hotels</li>
              <li><FaBus /> Private vehicle for all sightseeing and transfers</li>
              <li><FaAnchor /> Sunrise boat ride on River Ganga (shared basis)</li>
              <li><FaMapMarkerAlt /> All temple visits and sightseeing mentioned</li>
              <li><FaShieldAlt /> Driver allowance, toll taxes, parking, and fuel</li>
              <li><FaCheckCircle /> Tour coordination and assistance by Chandrama Holidays</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets</li>
              <li><FaTimesCircle /> Entry tickets to monuments or attractions</li>
              <li><FaTimesCircle /> Electric rickshaw charges in narrow lanes</li>
              <li><FaTimesCircle /> Personal expenses (Shopping, Tips, Laundry)</li>
              <li><FaShieldAlt /> Travel insurance and medical expenses</li>
              <li><FaTimesCircle /> Anything not mentioned in inclusions</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Tour Overview</h2>
          <p className="about-text">
            Experience the timeless spiritual essence of India through this sacred journey to Varanasi Ghats and Ayodhya, two of the most revered pilgrimage destinations in the country.
            <br /><br />
            Varanasi, one of the oldest living cities in the world, is known for its sacred ghats along the River Ganga, ancient temples, and the mesmerizing evening Ganga Aarti. Ayodhya, the divine birthplace of Lord Ram, is a deeply spiritual city filled with historic temples and sacred ghats.
            <br /><br />
            This carefully planned journey offers a perfect balance of temple darshan, cultural exploration, and spiritual experiences, allowing travelers to immerse themselves in the divine atmosphere of these holy cities.
          </p>
        </motion.section>

        {/* FOOTER LINKS */}
        <div className="footer-links-container" style={{marginTop: '40px'}}>
          <div className="accordion-item">
            <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>Terms & Conditions</span>
              <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
            </Link>
          </div>

          <div className="accordion-item">
            <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
              <span>Privacy Policy</span>
              <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
            </Link>
          </div>
        </div>

        {/* IMPORTANT NOTE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{width: '100%'}}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares. Electric rickshaw charges for narrow lanes must be paid directly locally.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default KashiAyodhya;